//* 멤버별 부고 발송을 위해 설정을 변경할 수 있고, 멤버별 부고발송여부등을 보여주는 카드
import { FC, useEffect, useRef, useState } from 'react';

import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { BugoReSendModal } from '@service/bugo/components/BugoReSendModal';
import { MemberBugoIFormInputs } from '@service/bugo/interfaces/memberBugoSend.interface';
import {
  bugoAttributionForCreate,
  bugoAttributionForUpdate,
} from '@service/bugo/utils/bugoAttribution.util';
import { createBugo, updateBugo } from '@shared/api/bugo/bugo.controller';
import { Bugo } from '@shared/api/bugo/bugo.interface';
import { CashAccount } from '@shared/api/cashAccount/cashAccount.interface';
import { FeventOne } from '@shared/api/fevent/fevent.interface';
import { updateMember } from '@shared/api/member/member.controller';
import { Member } from '@shared/api/member/member.interface';
import { Button } from '@shared/components/Button';
import Modal from '@shared/components/Modal';
import ResizableBox from '@shared/components/ResizableBox';
import { RoundedTag } from '@shared/components/RoundedTag';
import TelField from '@shared/components/TelField';
import {
  notificateError,
  notificateWarning,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { Control, UseFormRegister, UseFormSetValue, useWatch } from 'react-hook-form';

const DURATION = 0.3;
interface Props {
  fevent: FeventOne;
  member: Member<string, Bugo, CashAccount>;
  index: number;
  register: UseFormRegister<MemberBugoIFormInputs>;
  control: Control<MemberBugoIFormInputs, any>;
  setValue: UseFormSetValue<MemberBugoIFormInputs>;
  //* 부고전송 버튼의 전송 대상 체크 (체크되어 있으면 전송완료여도 재전송 대상)
  sendChecked?: boolean;
  onToggleSendCheck?: () => void;
}

export const MemberBugoSendCard: FC<Props> = function MemberBugoSendCard({
  fevent,
  member,
  index,
  register,
  control,
  setValue,
  sendChecked,
  onToggleSendCheck,
}: Props) {
  const { bugoRole, userProfile } = useAuth();
  const foldId = `memberBugoSendCard-${member._id}`;
  const [unfold, setUnFold] = useRecoilState(simpleOpenAtomFamily(foldId));
  const onUnFoldBtnClick = () => {
    setUnFold((curr) => !curr);
  };

  const reSendOpenId = `memberBugoSendCard-reSend-${member._id}`;
  const setReSendOpen = useSetRecoilState(simpleOpenAtomFamily(reSendOpenId));

  const phoneNumber =
    useWatch({
      control,
      name: `memberBugo.${index}.phoneNumber`,
      defaultValue: member.phoneNumber,
    }) ?? member.phoneNumber;

  const mournerText = useWatch({
    control,
    name: `memberBugo.${index}.mournerText`,
  });

  const [bugoId, setBugoId] = useState(member.bugo?._id ?? '');

  //* 부고장 표시 대상 — 이 상주의 부고장 명단에서 숨길 member 목록
  const hiddenMemberIds =
    useWatch({ control, name: `memberBugo.${index}.hiddenMemberIds` }) ??
    member.bugo?.hiddenMemberIds ??
    [];
  const orderedMembers = (fevent.memberOrderList ?? []).filter((item) => !!item);
  const hasOtherMembers = orderedMembers.some((item) => item._id !== member._id);
  //* 부고장(ObituaryMemberGrid)과 같은 순서의 관계별 줄 구성 — 모달 미리보기용
  const relationTypeList = Array.from(
    new Set(orderedMembers.map((item) => item.relationType)),
  );
  const membersByRelation = relationTypeList.map((relation) => ({
    relationType: relation,
    members: orderedMembers.filter((item) => item.relationType === relation),
  }));
  const hiddenMembers = orderedMembers.filter((item) =>
    hiddenMemberIds.includes(item._id),
  );
  const memberDisplayName = (item: Member<string, Bugo, CashAccount>) =>
    item.shortName || item.fullName || item.relationType;

  const hiddenMembersOpenId = `memberBugoSendCard-hiddenMembers-${member._id}`;
  const setHiddenMembersOpen = useSetRecoilState(
    simpleOpenAtomFamily(hiddenMembersOpenId),
  );

  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const hiddenSaveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const legacyServerWarnedRef = useRef(false);
  useEffect(() => () => clearTimeout(hiddenSaveTimerRef.current), []);

  const saveHiddenMemberIds = (next: string[]) => {
    setValue(`memberBugo.${index}.hiddenMemberIds`, next);

    //* 이미 발행된 부고장은 재전송 없이 바로 반영되도록 즉시 저장 (미발행이면 전송 시 함께 생성)
    if (!bugoId) return;
    clearTimeout(hiddenSaveTimerRef.current);
    hiddenSaveTimerRef.current = setTimeout(async () => {
      try {
        const updated = await updateBugo(
          { _id: bugoId, hiddenMemberIds: next },
          bugoRole(),
        );
        //* 구서버 안전장치: 스키마 반영 전이면 필드가 조용히 증발한다 — 응답으로 감지해 1회만 안내
        if (
          next.length > 0 &&
          updated.hiddenMemberIds === undefined &&
          !legacyServerWarnedRef.current
        ) {
          legacyServerWarnedRef.current = true;
          notificateWarning(
            notifiacationInstance,
            '표시 설정 서버 반영 대기',
            '서버 업데이트 후 부고장에 적용됩니다.',
          );
        }
      } catch {
        notificateError(notifiacationInstance, '표시 설정 저장 실패');
      }
    }, 600);
  };

  const onToggleHiddenMember = (targetMemberId: string) => {
    saveHiddenMemberIds(
      hiddenMemberIds.includes(targetMemberId)
        ? hiddenMemberIds.filter((id) => id !== targetMemberId)
        : [...hiddenMemberIds, targetMemberId],
    );
  };

  const updateMemberAndBugoBeforeSend = async () => {
    const memberUpdate = {
      _id: member._id,
      phoneNumber: phoneNumber,
    };

    await updateMember(memberUpdate, bugoRole());

    if (member?.bugo?._id) {
      const bugoUpdate = {
        _id: member.bugo?._id,
        mournerText: mournerText,
        hiddenMemberIds: hiddenMemberIds,
        //* 관리자(Admin) 대리 작업일 때만 상가(fevent) 귀속으로 정렬 (오귀속 교정)
        ...bugoAttributionForUpdate(fevent, userProfile),
      };
      await updateBugo(bugoUpdate, bugoRole());
    } else {
      if (userProfile) {
        const bugoCreate = {
          //* 관리자(Admin)가 대신 전송할 때만 상가의 담당 지도사·브랜드에 귀속
          //* (일반 role은 기존 그대로 로그인 계정 귀속)
          ...bugoAttributionForCreate(fevent, userProfile),
          fevent: member.fevent,
          member: member._id,
          mournerText: mournerText,
          hiddenMemberIds: hiddenMemberIds,
          schedule: {
            courtesySend: false,
          },
          config: {
            isEnable: true,
          },
        };
        const bugo = await createBugo(bugoCreate, bugoRole());
        setBugoId(bugo._id);
      }
    }
  };

  return (
    <ResizableBox
      duration={DURATION}
      className="rounded-2xl text-sm font-bold shadow-smd theme-bg-1"
    >
      <div className="flex items-center justify-between px-4 py-2">
        <div className="flex h-full flex-wrap items-center gap-1.5">
          {onToggleSendCheck && (
            <button
              type="button"
              aria-label="부고전송 대상 선택"
              onClick={onToggleSendCheck}
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 text-sm font-bold transition-colors ${
                sendChecked
                  ? 'text-white theme-border-main theme-bg-main'
                  : 'border-gray-300 text-gray-300'
              }`}
            >
              ✓
            </button>
          )}
          <h4 className="leading-7">{member.relationType}</h4>
          {member.fullName && (
            <RoundedTag text={member.fullName} className="theme-bg-5"></RoundedTag>
          )}
          {member.bugo?.lastSentPhoneNumber && (
            <RoundedTag
              text={sendChecked ? '재전송 대상' : '전송완료'}
              className="theme-bg-8"
            ></RoundedTag>
          )}
        </div>
        <div className="flex shrink-0">
          <Button
            type="button"
            onClick={() => onUnFoldBtnClick()}
            className={`button-no-border h-full whitespace-nowrap px-1 text-xs leading-5 ${
              !unfold ? 'theme-text-main' : 'theme-text-8'
            }`}
          >
            {!unfold ? '개인설정 ▾' : '접기'}
          </Button>
        </div>
      </div>
      <div className={`grid grid-cols-2 gap-2 px-2 pb-3 ${!unfold ? 'hidden' : 'block'}`}>
        <TelField
          value={phoneNumber}
          register={register}
          name={`memberBugo.${index}.phoneNumber`}
          className="w-full"
        />
        <Button
          className="button-rectangle h-full text-white theme-bg-main"
          onClick={async () => {
            //* bugo update 먼저
            await updateMemberAndBugoBeforeSend();
            setReSendOpen(true);
          }}
        >
          재전송
        </Button>
        <BugoReSendModal
          bugoId={bugoId}
          feventId={member.fevent}
          memberName={member.fullName}
          openId={reSendOpenId}
        />
        <div className="col-span-2 text-sm">
          <textarea
            placeholder={`개인별 상주말씀 (미작성시 고인정보에서 작성한 상주말씀이 적용됩니다.)`}
            className="input-box min-h-16 w-full p-2 font-medium theme-bg-1 theme-border-1 placeholder:theme-text-6"
            {...register(`memberBugo.${index}.mournerText`)}
          ></textarea>
        </div>
        {hasOtherMembers && (
          <div className="col-span-2">
            <button
              type="button"
              onClick={() => setHiddenMembersOpen(true)}
              className="flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left theme-border-1 theme-bg-1"
            >
              <span className="shrink-0 text-xs font-bold theme-text-9">
                부고장 표시 대상 설정
              </span>
              {hiddenMembers.length === 0 ? (
                <span className="text-xs font-medium theme-text-8">전체 표시 ▸</span>
              ) : (
                <span className="min-w-0 truncate text-xs font-bold theme-text-warn">
                  {hiddenMembers.length}명 제외:{' '}
                  {hiddenMembers.map(memberDisplayName).join(', ')} ▸
                </span>
              )}
            </button>
            <Modal openId={hiddenMembersOpenId} position="center">
              <div className="space-y-4">
                <div className="text-center">
                  <h3 className="text-base font-bold">부고장 표시 대상 설정</h3>
                  <p className="mt-1 text-xs theme-text-8">
                    {member.fullName ? `${member.fullName}님의 ` : '이 '}부고장에서
                    표시할 가족을 제거합니다
                  </p>
                  <p className="text-xs theme-text-8">
                    제거할 가족을 누르세요 (다시 누르면 복원)
                  </p>
                </div>
                {/* 부고장 명단과 같은 배치의 미리보기 */}
                <div className="rounded-lg border p-4 theme-border-1">
                  <div className="space-y-3">
                    {membersByRelation.map((group) => {
                      const allHidden = group.members.every((item) =>
                        hiddenMemberIds.includes(item._id),
                      );
                      return (
                        <div key={group.relationType} className="flex items-start gap-3">
                          <p
                            className={`w-10 shrink-0 pt-1.5 text-sm font-bold ${
                              allHidden
                                ? 'line-through opacity-40 theme-text-8'
                                : 'text-gray-500'
                            }`}
                          >
                            {group.relationType}
                          </p>
                          <div className="flex min-w-0 flex-wrap gap-1.5">
                            {group.members.map((item) => {
                              const isSelf = item._id === member._id;
                              const hidden = hiddenMemberIds.includes(item._id);
                              if (isSelf) {
                                return (
                                  <span
                                    key={item._id}
                                    className="rounded-md border px-2.5 py-1 text-sm font-medium theme-border-1 theme-text-9"
                                  >
                                    {memberDisplayName(item)}
                                    <span className="ml-1 text-xs theme-text-6">
                                      본인
                                    </span>
                                  </span>
                                );
                              }
                              return (
                                <button
                                  key={item._id}
                                  type="button"
                                  onClick={() => onToggleHiddenMember(item._id)}
                                  className={`rounded-md border px-2.5 py-1 text-sm font-medium transition-all ${
                                    hidden
                                      ? 'line-through opacity-40 theme-border-1 theme-text-8'
                                      : 'theme-border-main theme-text-9'
                                  }`}
                                >
                                  {memberDisplayName(item)}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="mt-3 text-xs font-medium theme-text-6">
                    관계의 가족을 모두 제거하면 그 줄도 표시되지 않습니다
                  </p>
                </div>
                <div
                  className={`grid gap-2 ${
                    hiddenMembers.length > 0 ? 'grid-cols-2' : ''
                  }`}
                >
                  {hiddenMembers.length > 0 && (
                    <Button
                      type="button"
                      className="button-rectangle text-white theme-bg-12"
                      onClick={() => saveHiddenMemberIds([])}
                    >
                      모두 표시
                    </Button>
                  )}
                  <Button
                    type="button"
                    className="button-rectangle text-white theme-bg-main"
                    onClick={() => setHiddenMembersOpen(false)}
                  >
                    확인
                  </Button>
                </div>
              </div>
            </Modal>
          </div>
        )}
      </div>
    </ResizableBox>
  );
};
